import React, { useRef, useState } from 'react';
import ReactDOM, { createPortal } from 'react-dom';
import styles from './progress.module.scss';
import Typography from '../../../../../../components/Typography';

interface ProgressProps {
  currentTime: number;
  duration: number;
  sections: number[]; // Array of section start times in seconds, sorted ascending
  sectionTitles: string[]; // Array of section titles, same order as sections
  onSeek: (time: number) => void;
}

const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));

const Progress: React.FC<ProgressProps> = ({ currentTime, duration, sections, sectionTitles, onSeek }) => {
  const barRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [dragTime, setDragTime] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ left: number, top: number }>({ left: 0, top: 0 });

  // Calculate segment widths
  const allSections = [...sections, duration];
  const segments = allSections.slice(1).map((end, i) => {
    const start = allSections[i];
    let width = ((end - start) / duration) * 100;
    if (!isFinite(width) || width < 0) width = 0;
    return {
      start,
      end,
      width,
    };
  });

  // Find the current section index for a given time
  const getSectionIndex = (time: number) => {
    for (let i = sections.length - 1; i >= 0; i--) {
      if (time >= sections[i]) return i;
    }
    return 0;
  };

  const thumbTime = dragging && dragTime !== null ? dragTime : currentTime;
  const thumbPercent = clamp((thumbTime / duration) * 100, 0, 100);
  const progressPercent = clamp((currentTime / duration) * 100, 0, 100);
  const sectionIndex = getSectionIndex(thumbTime);
  const sectionTitle = sectionTitles[sectionIndex] || '';

  // Handle drag
  const getTimeFromEvent = (e: React.MouseEvent | React.TouchEvent) => {
    const bar = barRef.current;
    if (!bar) return 0;
    const rect = bar.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const x = clamp(clientX - rect.left, 0, rect.width);
    return clamp((x / rect.width) * duration, 0, duration);
  };

  const handlePointerDown = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setDragging(true);
    const time = getTimeFromEvent(e);
    setDragTime(time);
    document.body.style.userSelect = 'none';
  };

  const handlePointerMove = (e: MouseEvent | TouchEvent) => {
    if (!dragging) return;
    const bar = barRef.current;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
    const x = clamp(clientX - rect.left, 0, rect.width);
    const time = clamp((x / rect.width) * duration, 0, duration);
    setDragTime(time);
  };

  const handlePointerUp = () => {
    if (dragging && dragTime !== null) {
      onSeek(dragTime);
    }
    setDragging(false);
    setDragTime(null);
    document.body.style.userSelect = '';
  };

  const updateTooltipPosition = () => {
    const thumb = thumbRef.current;
    if (thumb) {
      const thumbRect = thumb.getBoundingClientRect();
      setTooltipPos({
        top: thumbRect.bottom - 10,
        left: thumbRect.left - 10,
      });
    }
  };

  React.useEffect(() => {
    if (!dragging) return;
    const move = (e: MouseEvent | TouchEvent) => handlePointerMove(e);
    const up = () => handlePointerUp();
    window.addEventListener('mousemove', move);
    window.addEventListener('touchmove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchend', up);
    };
  }, [dragging, dragTime]);

  // Always update tooltip position when thumb moves
  React.useLayoutEffect(() => {
    updateTooltipPosition();
  }, [thumbPercent, dragging, dragTime]);


  if (!duration || duration <= 0) {
    return <div className={styles.root}><div className={styles.segments} /></div>;
  }

  return (
    <div
      className={styles.root}
      ref={barRef}
      onMouseDown={handlePointerDown}
      onTouchStart={handlePointerDown}
      style={{ position: 'relative' }}
    >
      <div
        className={styles.progressBackground}
      />
      <div
        className={styles.progressFill}
        style={{ width: `${progressPercent}%` }}
      />
      <div className={styles.segments}>
        {segments.map((seg, i) => (
          <div
            key={i}
            className={styles.segment}
            style={{ width: `${seg.width}%` }}
          />
        ))}
      </div>

      <div
        className={styles.thumbContainer}
        ref={thumbRef}
        style={{ left: `calc(${thumbPercent}% - 8px)` }}
        onMouseDown={e => { e.stopPropagation(); handlePointerDown(e); }}
        onTouchStart={e => { e.stopPropagation(); handlePointerDown(e); }}
        role="slider"
        aria-valuenow={thumbTime}
        aria-valuemin={0}
        aria-valuemax={duration}
        tabIndex={0}
      >
        <div className={styles.thumb} />

        <div className={styles.tooltip}>
          <Typography variant="p" className={styles.tooltip_text}>
            {sectionTitle}
          </Typography>
        </div>
      </div>

    </div>
  );
};

export default Progress;
